<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessCardsGrid from "~/components/molecules/WrDesignProcessCardsGrid.vue";
import WrDesignTailoredSection from "~/components/molecules/WrDesignTailoredSection.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrIcon from "~/components/global/WrIcon.vue";

import WrDesignScenarioCards from "~/components/molecules/WrDesignScenarioCards.vue";
import WrDesignWhyItMattersSection from "~/components/molecules/WrDesignWhyItMattersSection.vue";
import WrDesignServiceCardsSection from "~/components/molecules/WrDesignServiceCardsSection.vue";
import WrDesignWhyChooseUsCard from "~/components/molecules/WrDesignWhyChooseUsCard.vue";
import WrDesignTwoByTwoCardsSection from "~/components/molecules/WrDesignTwoByTwoCardsSection.vue";
import WrDesignReasonsSection from "~/components/molecules/WrDesignReasonsSection.vue";
import WrDesignReadyToElevateYourBrand from "~/components/molecules/WrDesignReadyToElevateYourBrand.vue";
import WrDesignBenefitCards from "~/components/molecules/WrDesignBenefitCards.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrTestimonialCardCarousel from "~/components/molecules/WrTestimonialCardCarousel.vue";

const LazyWrDesignScenarioCards = WrDesignScenarioCards;
const LazyWrDesignWhyItMattersSection = WrDesignWhyItMattersSection;
const LazyWrDesignServiceCardsSection = WrDesignServiceCardsSection;
const LazyWrDesignWhyChooseUsCard = WrDesignWhyChooseUsCard;
const LazyWrDesignTwoByTwoCardsSection = WrDesignTwoByTwoCardsSection;
const LazyWrDesignReasonsSection = WrDesignReasonsSection;
const LazyWrDesignReadyToElevateYourBrand = WrDesignReadyToElevateYourBrand;
const LazyWrDesignBenefitCards = WrDesignBenefitCards;
const LazyWrDesignFaqsSection = WrDesignFaqsSection;
const LazyWrDesignStatsCard = WrDesignStatsCard;
const LazyWrTestimonialCardCarousel = WrTestimonialCardCarousel;
const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/solutions/club-management-software-development-services-content.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  processMotion,
  techStack,
  stackIntegration,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Solutions", item: "https://webreinvent.com/solutions" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessCardsGrid: { title, description, svgIcon }
const quickHitCards = computed(() =>
  quickHits.items.map((item: any) => ({
    title: item.title,
    description: item.description,
    svgIcon: item.svgIcon,
  }))
);

// TailoredSection: { svgIcon, title, description }
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// ScenarioCards: { title, problem, solution, result }
const capabilityCards = computed(() =>
  capabilities.items.map((card: any) => ({
    title: card.title,
    problem: card.problem,
    solution: card.solution,
    result: card.result,
  }))
);

// WhyItMattersSection: { title, description, svgIcon }
const processMotionCards = computed(() =>
  processMotion.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ServiceCardsSection: { svgIcon, title, desc }
const techStackCards = computed(() =>
  techStack.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.desc,
  }))
);

// TwoByTwoCardsSection: { id, title, description, svgIcon }
const integrationCards = computed(() =>
  integrationCardsData.items.map((card: any) => ({
    id: card.number,
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ReasonsSection: { title, desc } + svgIcons[]
const useCaseCards = computed(() =>
  useCases.items.map((card: any) => ({
    title: card.title,
    desc: card.desc,
  }))
);
const useCaseIcons = computed(() => useCases.svgIcons);

// ReadyToElevateYourBrand: { svgIcon, title, description }
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// BenefitCards: { svgIcon, title, description }
const ctaCards = computed(() =>
  cta.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.desc,
  }))
);

const faqItems = computed(() => faq.faqItems);

const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card (shared) -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text (shared) -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (ProcessCardsGrid - Restaurant only, 3-col grid) -->
      <section aria-label="Club System Capabilities at a Glance">
        <WrDesignProcessCardsGrid
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :cards="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in quickHitCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignProcessCardsGrid>
      </section>

      <!-- Section 3: Business Outcomes (TailoredSection - Restaurant only, 3-col grid) -->
      <section aria-label="Business Outcomes">
        <WrDesignTailoredSection
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
          :cta-text="businessOutcomes.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 4: Problem / Solution (shared) -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (ScenarioCards - neither, 3-col problem/solution/result) -->
      <section aria-label="Club System Capabilities">
        <LazyWrDesignScenarioCards
          :heading="capabilities.heading"
          :subcopy="capabilities.subcopy"
          :cards="capabilityCards"
        />
      </section>

      <!-- Section 6: How the System Works (WhyItMattersSection - single row, 4 cards) -->
      <section aria-label="Club System Workflow">
        <LazyWrDesignWhyItMattersSection
          :heading="processMotion.heading"
          :subcopy="processMotion.subcopy"
          :cards="processMotionCards"
          :cta-text="processMotion.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 7: Tech Stack (ServiceCardsSection - Restaurant only, 3-col grid, left border) -->
      <section aria-label="Enterprise Architecture">
        <LazyWrDesignServiceCardsSection
          :heading-line1="techStack.headingLine1"
          :heading-line2="techStack.headingLine2"
          :subcopy="techStack.subcopy"
          :cards="techStackCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 8: Integration Banner (WhyChooseUsCard - shared) -->
      <!-- <section aria-label="Integration Stack">
        <LazyWrDesignWhyChooseUsCard
          :heading="stackIntegration.heading"
          :subcopy="stackIntegration.subcopy"
          :cta-text="stackIntegration.ctaText"
          :image-dark-src="stackIntegration.imageDarkSrc"
          :image-light-src="stackIntegration.imageLightSrc"
          @cta-clicked="openModal"
        />
      </section> -->

      <!-- Section 9: Integration Cards (TwoByTwoCardsSection - CRM only, 2x2 grid) -->
      <section aria-label="Integrations and Systems">
        <LazyWrDesignTwoByTwoCardsSection
          :heading="integrationCardsData.singleLineHeading"
          :subcopy="integrationCardsData.subcopy"
          :cards="integrationCards"
          :cta-text="integrationCardsData.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in integrationCards" :key="card.title" #[`icon-${card.id}`]>
            <WrIcon :name="card.svgIcon" class="size-6" />
          </template>
        </LazyWrDesignTwoByTwoCardsSection>
      </section>

      <!-- Section 10: Real-World Use Cases (ReasonsSection - CRM only, 3+2 layout) -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Real-World Use Cases">
        <LazyWrDesignReasonsSection
          :heading="useCases.heading"
          :subcopy="useCases.subcopy"
          :cards="useCaseCards"
          :svg-icons="useCaseIcons"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 11: Why Choose Us (ReadyToElevateYourBrand - Restaurant only, horizontal row) -->
      <section aria-label="Why Choose WebReinvent">
        <LazyWrDesignReadyToElevateYourBrand
          :heading="whyChooseUs.heading"
          :subcopy="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 12: CTA Block (BenefitCards - CRM only, 2-col grid) -->
      <section aria-label="Call to Action">
        <LazyWrDesignBenefitCards
          :heading="cta.heading"
          :subcopy="cta.subcopy"
          :cards="ctaCards"
        />
        <div v-if="cta.ctaText" class="flex justify-center mt-8">
          <WrButton
            @click="openModal"
            container-class="h-[48px]"
            append-class="text-base font-normal px-6"
          >
            {{ cta.ctaText }}
          </WrButton>
        </div>
      </section>

      <!-- Section 13: Testimonials -->
      <section class="relative mt-16 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
        <h2 class="text-center mb-11">
          <WrGradientText text="Trusted for excellence and efficiency" />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
        </h2>
        <LazyWrTestimonialCardCarousel />
        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
          View All
        </WrButton>
      </section>

      <!-- Section 14: Stats Card (shared, lazy loaded) -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <LazyWrDesignStatsCard />
      </section>

      <!-- Section 13: FAQ (shared) -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Frequently asked questions">
        <LazyWrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>
    </div>
  </main>

  <LazyWrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
